import type { ReactNode } from "react";
import { useMemo, useSyncExternalStore } from "react";
import type { ImageSourcePropType } from "react-native";
import { HStack } from "../../ui/hstack";
import { Spinner } from "../../ui/spinner";
import { Text } from "../../ui/text";
import ElapsedHint from "./ElapsedHint";
import MomentShell from "./MomentShell";
import PricingSparkline from "./PricingSparkline";
import ThumbnailDeck from "./ThumbnailDeck";
import type { AgentTurnStore } from "./turnStore";
import type { AgentToolCall } from "./types";
import useElapsed from "./useElapsed";

/** The glass illustrations the moments are built around. Supplied by the app
 * rather than imported here: the kit itself has no assets of its own, and the
 * founder's PNGs live in the consuming app (`apps/center/assets/illustrations`)
 * so other apps that mount this chat can keep working without them — a moment
 * whose image is missing simply renders nothing where the glass icon would be. */
export type AgentIllustrations = {
  camera: ImageSourcePropType;
  card: ImageSourcePropType;
  clock: ImageSourcePropType;
};

type Family = "research" | "pricing";

/** The tool names behind the moments while the chat-edit agent works. */
const PRICING_TOOL = "recommend_pricing";
/** get_research_results: reading back what the background content research found. */
const RESEARCH_TOOLS = new Set(["get_research_results"]);

const familyOf = (toolName: string): Family | null => {
  if (RESEARCH_TOOLS.has(toolName)) return "research";
  if (toolName === PRICING_TOOL) return "pricing";
  return null;
};

/**
 * The most recently STARTED family among this turn's tool calls.
 *
 * "The most recently activated moment wins", per the artifact: scanning from the
 * end of the trail means a newer call always beats an older one, whether or not
 * the older one has finished — which is also what makes the previous moment
 * unmount the instant a new one starts, with no extra bookkeeping.
 */
const currentFamilyOf = (tools: AgentToolCall[]): Family | null => {
  for (let index = tools.length - 1; index >= 0; index -= 1) {
    const family = familyOf(tools[index].toolName);
    if (family) return family;
  }
  return null;
};

const RESEARCH_STAGE_TITLE: Record<string, string> = {
  QUEUED: "Preparando la investigación…",
  SCRAPING: "Analizando contenido ganador…",
  ANALYZING: "Analizando contenido ganador…",
  SCORING: "Calculando puntajes…",
  FALLBACK: "Buscando referentes de la industria…",
};

const RESEARCH_PLACEHOLDER_STAGES = new Set([
  "SCRAPING",
  "ANALYZING",
  "SCORING",
]);

type Props = {
  tools: AgentToolCall[];
  store: AgentTurnStore;
  /** Whether the turn is still live — moments 1-3 only ever show while it is. */
  isStreaming: boolean;
  /** Feeds the elapsed-time cue. 0 while idle. */
  startedAt: number;
  illustrations?: AgentIllustrations;
  /**
   * Moment 4 — "Draft creado". Supplied by the consumer rather than built here:
   * it needs the campaign draft's own data (its title, its contents, its budget),
   * which this generic chat kit has no business knowing about. Outranks every
   * other moment, and — unlike them — is not gated on the turn being live: once a
   * draft is ready, the card stays up through however many more turns it takes to
   * actually publish it.
   */
  finalMoment?: ReactNode | null;
};

/**
 * What `useAnimationMoment` renders, plus a stable identity for it.
 *
 * `key` is `null` exactly when `node` is `null`, and otherwise names which
 * moment is on screen ("search", "research", "pricing", …) — it changes when,
 * and only when, the card itself is swapped for a different one, not on every
 * internal update (a stage rotating, a thumbnail arriving) of the same card.
 * `AgentChat` uses it to force a scroll the instant a card appears, without
 * re-deriving "is this the same moment as before" from `node` itself, which
 * has no identity of its own to compare.
 */
export type AnimationMoment = { node: ReactNode; key: string | null };

const NO_MOMENT: AnimationMoment = { node: null, key: null };

/**
 * Animation system v2 — at most ONE "moment" card on screen at any time, full
 * chat-column width.
 *
 * Replaces the old stack of `ToolActivity` + `SearchProductsActivity` +
 * `PricingActivity` + `ResearchActivity` + `EvidenceStack`: those made every tool
 * call legible at once, which the founder's verdict was that a wall of small cards
 * competing for attention reads as noise, not progress. This picks the single most
 * recently activated moment and gives it the whole width.
 *
 * A HOOK (`useAnimationMoment`), not just a component: `AgentChat` needs the exact
 * same "is there a moment on screen right now" answer to decide whether to fall
 * back to `ThinkingIndicator` — computing that a second time with a looser
 * heuristic (e.g. "are there any tools yet") is how the chat ends up with a dead
 * gap where neither a moment nor a thinking bubble is showing.
 */
export const useAnimationMoment = ({
  tools,
  store,
  isStreaming,
  startedAt,
  illustrations,
  finalMoment,
}: Props): AnimationMoment => {
  const research = useSyncExternalStore(
    store.subscribeResearch,
    store.getResearch,
  );
  const elapsedMs = useElapsed(startedAt);

  const currentFamily = useMemo(() => currentFamilyOf(tools), [tools]);

  // Moment 4 always wins, and is the only moment that survives an idle turn — it
  // is not "in progress" news, it is the outcome.
  if (finalMoment) return { node: <>{finalMoment}</>, key: "final" };

  // Every other moment is "in progress" news — a turn that has gone idle means
  // there is nothing live to narrate.
  if (!isStreaming) return NO_MOMENT;

  const inGap = !currentFamily;

  // The research card outlives its own tool calls: once a job exists, its
  // evidence (the thumbnail deck) stays on screen through the streaming turns
  // between "research started" and the concepts being presented. It retires FOR
  // GOOD once the job is done AND the turn that was showing it has settled
  // (`research.retired`, flipped by `retireResearchOnSettle` — see turnReducer),
  // so it does not resurface in every later streaming gap and read as the flow
  // running backwards.
  const researchFallback =
    inGap && research.jobId !== null && !research.failed && !research.retired;

  if (!currentFamily && !researchFallback) return NO_MOMENT;

  if (currentFamily === "research" || researchFallback) {
    const active = !research.done;
    const title = research.failed
      ? "No se pudo completar la investigación"
      : research.done
        ? "Análisis listo — Top 3 encontrados"
        : (RESEARCH_STAGE_TITLE[research.stage ?? ""] ??
          "Analizando contenido ganador…");
    const placeholder =
      !research.done && RESEARCH_PLACEHOLDER_STAGES.has(research.stage ?? "");
    const detail = research.messages.at(-1);

    return {
      key: "research",
      node: (
        <MomentShell
          key="research"
          image={illustrations?.camera}
          active={active}
          title={title}
        >
          {research.failed ? (
            <Text className="mt-3 text-sm text-error-600">
              Vuelve a intentarlo desde el chat.
            </Text>
          ) : (
            <>
              {detail ? (
                <Text
                  className={`mt-3 text-sm ${
                    active
                      ? "text-typography-600 animate-shimmer motion-reduce:animate-none"
                      : "text-typography-500"
                  }`}
                >
                  {detail}
                </Text>
              ) : null}
              <ThumbnailDeck
                thumbnails={research.thumbnails}
                placeholder={placeholder}
                settled={research.done}
              />
              {research.done ? (
                // The analysis is done but the turn is still going: the model is
                // writing the three concept proposals right now. A small live
                // loader keeps the card moving instead of freezing on "listo".
                <HStack space="xs" className="mt-3 items-center justify-center">
                  <Spinner size="small" />
                  <Text className="text-xs text-typography-500 animate-shimmer motion-reduce:animate-none">
                    Creando propuestas de guiones…
                  </Text>
                </HStack>
              ) : null}
            </>
          )}
          {!research.done ? (
            <ElapsedHint elapsedMs={elapsedMs} className="mt-3" />
          ) : null}
        </MomentShell>
      ),
    };
  }

  // currentFamily === "pricing".
  const relevant = tools.filter((tool) => tool.toolName === PRICING_TOOL);
  const active = relevant.some((tool) => !tool.done);
  return {
    key: "pricing",
    node: (
      <MomentShell
        key="pricing"
        image={illustrations?.card}
        badgeImage={illustrations?.clock}
        active={active}
        title="Buscando el precio justo…"
      >
        <Text className="mt-3 text-sm text-typography-500">
          Cruzando el histórico de colaboraciones pagadas en Montu…
        </Text>
        <PricingSparkline active={active} />
        {active ? <ElapsedHint elapsedMs={elapsedMs} className="mt-3" /> : null}
      </MomentShell>
    ),
  };
};
